import { createPortal } from "react-dom";
import { useLayoutEffect, useState } from "react";

type Props = {
  open: boolean;
  anchorRect: DOMRect | null;
  onClose: () => void;
  children: React.ReactNode;
};

/**
 * Menu flutuante acoplado ao botão de ações (⋯), renderizado em document.body
 * para não ser recortado por overflow em tabelas/cards.
 */
export function ActionMenuPortal({ open, anchorRect, onClose, children }: Props) {
  const [pos, setPos] = useState({ top: 0, left: 0 });

  useLayoutEffect(() => {
    if (!open || !anchorRect) return;
    const w = 180;
    const left = Math.max(8, Math.min(anchorRect.right - w, window.innerWidth - w - 8));
    setPos({ top: anchorRect.bottom + 4, left });
  }, [open, anchorRect]);

  useLayoutEffect(() => {
    if (!open) return;
    const onScroll = () => onClose();
    window.addEventListener("scroll", onScroll, true);
    const onResize = () => onClose();
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onResize);
    };
  }, [open, onClose]);

  if (!open || !anchorRect || typeof document === "undefined") return null;

  return createPortal(
    <>
      <div
        data-action-menu-backdrop
        className="fixed inset-0 z-[500]"
        aria-hidden
        onMouseDown={(e) => {
          e.preventDefault();
          onClose();
        }}
      />
      <div
        data-action-menu-portal
        className="fixed z-[510] min-w-[140px] rounded-md border border-gray-200 bg-white py-1 text-gray-900 shadow-xl"
        style={{ top: pos.top, left: pos.left }}
        onMouseDown={(e) => e.stopPropagation()}
        role="menu"
      >
        {children}
      </div>
    </>,
    document.body,
  );
}
